import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { compose, withState, lifecycle } from 'recompose';
import { props, all } from 'ramda';

import Text from 'app/components/layout/Text.styled';
import S from 'app/features/flashMessage/FlashMessage.styled';
import { ANIMATION_DURATION } from 'app/features/flashMessage/constants';
import { clearFlashMessage } from 'app/features/flashMessage/actions';
import {
  messageId,
  statusType,
  timeoutExpiration,
} from 'app/features/flashMessage/selectors';
import connect from 'app/libs/connect';
import renderIf from 'app/enhancers/renderIf';

const UI = ({ statusType, messageKey, isActive, setIsVisible, onClose }) => {
  const animationDirection = isActive ? 'down' : 'up';

  return (
    <S.FlashMessage
      data-t="flash-message"
      type={statusType}
      animationDirection={animationDirection}
    >
      <Text size="l" as={S.FlashMessage.Text}>
        <FormattedMessage id={messageKey} />
      </Text>
      <S.FlashMessage.Close>
        <S.FlashMessage.Icon
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          aria-labelledby="cross-svg"
          data-t="flash-message-close-icon"
          role="img"
          type={statusType}
          onClick={() => {
            onClose();
            setIsVisible(false);
          }}
        >
          <title id="cross-svg">
            <FormattedMessage id="flash_message_icon_close" />
          </title>
          <path d="M13.41 12l5.3-5.3a1 1 0 10-1.42-1.4L12 10.58l-5.3-5.3a1 1 0 10-1.4 1.42L10.58 12l-5.3 5.3a1 1 0 000 1.4 1 1 0 001.42 0L12 13.42l5.3 5.3a1 1 0 101.4-1.42L13.42 12z" />
        </S.FlashMessage.Icon>
      </S.FlashMessage.Close>
    </S.FlashMessage>
  );
};

UI.propTypes = {
  statusType: PropTypes.oneOf(['error', 'success', 'neutral']).isRequired,
  onClose: PropTypes.func.isRequired,
  messageKey: PropTypes.string.isRequired,
  setIsVisible: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
};

let timeout = null;

function initTimeout({ timeoutExpiration, onClose, setIsVisible }) {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    onClose();

    setTimeout(() => setIsVisible(false), ANIMATION_DURATION);
  }, timeoutExpiration);
}

function componentDidMount() {
  initTimeout(this.props);
}

function componentWillUnmount() {
  clearTimeout(timeout);
  timeout = null;
}

function componentDidUpdate(prevProps) {
  const {
    messageId,
    timeoutExpiration,
    setIsActive,
    setMessageKey,
    isActive,
    setIsVisible,
  } = this.props;
  const { messageId: prevMessageId, timeoutExpiration: prevTimeoutExpiration } =
    prevProps;

  const updateState = () => {
    if (messageId && messageId !== prevMessageId) {
      setMessageKey(messageId);
      setIsVisible(true);
      setIsActive(true);
    }

    if (!messageId && isActive) {
      setIsActive(false);
    }
  };

  if (!messageId) {
    clearTimeout(timeout);
    updateState();
  }

  if (
    (messageId && messageId !== prevMessageId) ||
    timeoutExpiration !== prevTimeoutExpiration
  ) {
    initTimeout(this.props);
    updateState();
  }
}

const state = { messageId, statusType, timeoutExpiration };
const actions = { onClose: clearFlashMessage };

const FlashMessage = compose(
  connect(state, actions),
  withState('messageKey', 'setMessageKey', ''),
  withState('isActive', 'setIsActive', false),
  withState('isVisible', 'setIsVisible', true),
  lifecycle({ componentDidMount, componentDidUpdate, componentWillUnmount }),
  renderIf(compose(all(Boolean), props(['messageKey', 'isVisible']))),
)(UI);

export { UI, initTimeout, componentDidUpdate };

export default FlashMessage;
